import React from 'react';

import {
  Breakpoints,
  CTA,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  ThemeProvider,
  useIsMobile,
} from '@vw-marketing/us-components';

import {
  StyledConfirmButtonsContainer,
  StyledConfirmInformationContainer,
  StyledDeactivateAccountConfirmationContainer,
} from './styled';
import { FeatureAppTrackingManager } from '../../../../shared/typings/tagging';
import { onVWBasicButtonClickWithProcessEvent } from '../../../../shared/tagging/tagging-helpers';
import { RichTextRenderer } from '../../../../shared/utils/richTextRenderer';

export interface RemoveVehicleConfirmationViewProps {
  translationHandler: (key: string) => string;
  trackingManager: FeatureAppTrackingManager;
  userRole?: string;
  vehicleModelYear?: string;
  vehicleModelName?: string;
  handleConfirm: () => void;
  goBack: () => void;
}

export const RemoveVehicleConfirmationView = ({
  translationHandler,
  trackingManager,
  userRole,
  vehicleModelYear,
  vehicleModelName,
  handleConfirm,
  goBack,
}: RemoveVehicleConfirmationViewProps) => {
  const isDesktop: boolean = !useIsMobile(Breakpoints.b1920);

  const handleConfirmClick = () => {
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details - Remove Vehicle',
        StepNumber: '5.2',
      }, {
        name: 'Remove Vehicle',
      });
    } catch (error) {
      console.warn('Error tracking remove vehicle button click:', error);
    }
    handleConfirm();
  };

  const handleGoBackClick = () => {
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details - Remove Vehicle',
        StepNumber: '5.2',
      }, {
        name: 'Go Back',
      });
    } catch (error) {
      console.warn('Error tracking go back button click:', error);
    }
    goBack();
  };

  const descriptionKey = userRole === 'primary'
    ? 'vehicleManagement.removeVehicle.confirmation.description'
    : 'vehicleManagement.removeVehicle.confirmation.description.additional';

  return (
    <ThemeProvider theme="main">
      <StyledDeactivateAccountConfirmationContainer>
        <StyledConfirmInformationContainer>
          <TextCustom
            appearance={TextAppearance.copy200}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                color: TextColor.primary,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1920]: {
                fontSize: TextSize.TextSize20,
                fontWeight: TextWeight.regular,
                color: TextColor.primary,
                lineHeight: TextLineHeight.TextLineHeight32,
              },
            }}
          >
            <RichTextRenderer
              text={translationHandler(descriptionKey)}
              variables={{
                year_model: {
                  value: `${vehicleModelYear || ''} ${vehicleModelName || ''}`,
                  noTranslate: true,
                },
              }}
              boldStyle={{ fontWeight: TextWeight.bold }}
              noTranslateTerms={['myVW']}
            />
          </TextCustom>
        </StyledConfirmInformationContainer>
        <StyledConfirmButtonsContainer>
          <CTA tag="button" stretchContent={!isDesktop} onClick={handleConfirmClick}>
            {translationHandler('vehicleManagement.removeVehicle.confirmation.button')}
          </CTA>
          <CTA
            emphasis="secondary"
            onClick={handleGoBackClick}
            stretchContent={!isDesktop}
            tag="button"
          >
            {translationHandler('vehicleManagement.removeVehicle.confirmation.button.goBack')}
          </CTA>
        </StyledConfirmButtonsContainer>
      </StyledDeactivateAccountConfirmationContainer>
    </ThemeProvider>
  );
};
